// Benachrichtigungs-Balken — EIN Baustein für alle Verwaltungsseiten.
//
// ⚠️ Vorher war dieser Balken zwölfmal von Hand abgeschrieben. Nicht auf zwölf Seiten: acht Seiten
// plus die Mediathek, aber Alben, Interpreten und Playlists rendern ihn JE ZWEIMAL — einmal im
// Formular-Zweig, einmal im Listen-Zweig, mit identischer Klassenkette. Genau so entsteht Drift:
// wer eine der beiden Stellen anfasst, sieht die andere nicht.
//
// Gemessener Stand vor dem Zusammenlegen:
//
// | Ort                            | Fläche                                   | Auto-Ausblenden      | Schließen | Live-Region |
// |--------------------------------|------------------------------------------|----------------------|-----------|-------------|
// | Alben / Interpreten / Playlists (je 2×) | `p-4 rounded-lg`                | 4000 ms, BEIDE Typen | nein      | nein        |
// | Audio / Video / MindBooks      | `p-4 rounded-lg`                         | 3000 ms, BEIDE Typen | nein      | nein        |
// | Themen-Kategorien              | `fixed top-4 right-4 z-50 shadow-lg` + Rand, `text-*-800` | 3000 ms, BEIDE Typen | ja (`XMarkIcon`) | nein |
// | Benutzer                       | `flex items-start justify-between gap-4 p-4 rounded-lg` | 3000 ms, NUR Erfolg | ja, nur bei Fehler | ja |
// | Mediathek (BunnyCDNManager)    | eigene CSS-Insel `.notification`, Hex-Farben `#d4edda`/`#f8d7da`, `4px` Radius | 3000 ms, BEIDE Typen | nein | nein |
//
// **Optik = Mehrheitsfassung.** `p-4 rounded-lg` mit `bg-success-100 text-success-700` bzw.
// `bg-error-100 text-error-700`, im Textfluss stehend. Neun der zwölf Stellen fahren exakt das.
// Der `flex`-Rahmen der Benutzer-Verwaltung kommt dazu, weil ohne ihn der Schließen-Knopf keinen
// Platz hat; bei reinem Text rendert er sichtbar gleich.
//
// **Verhalten = bewusste Angleichung NACH OBEN, keine versehentliche.** Elf der zwölf Stellen
// blenden auch Fehler nach drei bis vier Sekunden aus. Das ist der eigentliche Fehler in diesen
// elf Kopien und wird hier NICHT übernommen: eine Fehlermeldung nach drei Sekunden wegzuräumen
// nimmt genau der Person die Information weg, die sie braucht — wer die Tabelle mit der Tastatur
// bedient oder kurz woanders hinsieht, erfährt sonst nie, dass der Schreibvorgang gescheitert ist;
// die Zeile steht danach unverändert da und sieht aus wie ein Erfolg. Übernommen wird deshalb die
// Semantik aus `UserManagement.tsx`: Erfolg verblasst, Fehler bleibt und trägt einen
// Schließen-Knopf, und beide melden sich als Live-Region an.
//
// **Was beim Angleichen verloren geht** (bewusst, damit es nachlesbar bleibt):
// - *Themen-Kategorien* verliert seinen eigenen Rand und das dunklere `text-*-800`, und der
//   Schließen-Knopf ist nicht mehr ein Symbol ohne Namen (`XMarkIcon`), sondern der beschriftete
//   Knopf der Mehrheitsfassung. Seine schwebende Position BLEIBT — sie ist jetzt die Eigenschaft
//   `schwebend` dieses Bausteins, siehe deren Begründung unten.
// - *Mediathek* verliert ihre eigene CSS-Insel (`padding: 12px 16px`, `border-radius: 4px`,
//   `font-size: 14px`, Bootstrap-Hex-Farben). Die Insel selbst löst dieser Baustein nicht auf —
//   die Regeln `.notification`, `.notification.success` und `.notification.error` im `<style>`-Block
//   von `BunnyCDNManager.tsx` werden beim Umstellen unbenutzt und gehören dort gelöscht.
// - *Alben / Interpreten / Playlists* verlieren die vierte Sekunde Anzeigedauer (4000 → 3000 ms).
//
// Die Zeitmessung selbst steht NICHT hier, sondern in `useBenachrichtigung` — sonst schreibt die
// nächste Seite ihren `setTimeout` wieder von Hand ab, und genau das war die Ursache dafür, dass
// drei der Kopien ihren Timer ohne `clearTimeout` aufräumen (Alben, Interpreten, Playlists).
import React from 'react';

/** Die beiden Tonlagen. Genau diese Werte trugen alle zwölf Kopien. */
export type BenachrichtigungsTyp = 'success' | 'error';

export interface BenachrichtigungsInhalt {
  type: BenachrichtigungsTyp;
  message: string;
}

interface BenachrichtigungProps {
  /** Was gemeldet wird. `null` heißt: keine sichtbare Meldung. */
  inhalt: BenachrichtigungsInhalt | null;
  /** Räumt die Meldung weg. Hängt am Schließen-Knopf, den nur der Fehler bekommt. */
  onSchließen: () => void;
  /**
   * Legt den Balken über die Seite (`position: fixed`, oben rechts) statt in den Textfluss.
   *
   * ⚠️ Das ist KEIN Rückschritt zur Handkopie der Themen-Kategorien, sondern eine Variante DIESES
   * Bausteins — eine Eigenschaft, kein zweiter Balken. Ohne sie gibt es einen echten Fehlerfall:
   *
   * Seiten, deren Formulare Modals sind (`fixed inset-0 … z-50`), lassen das Modal im Fehlerzweig
   * bewusst OFFEN — `setShowMainTopicForm(false)` steht dort im `try`, nicht im `catch`, damit die
   * Eingabe der Nutzerin nicht verloren geht. Ein Balken im Seitenfluss rendert dann hinter dem
   * Overlay und außerhalb des Sichtfelds: die Nutzerin speichert ein Subthema, der Server lehnt ab
   * (doppelter Schlüssel), sichtbar bleibt nur das unveränderte Formular — es sieht aus, als sei
   * der Klick ins Leere gegangen.
   *
   * Deshalb `z-[60]` und nicht `z-50`: der Balken muss ÜBER dem Modal-Overlay liegen, das selbst
   * `z-50` trägt. Bei Gleichstand entschiede die Dokumentreihenfolge — und der Balken steht vor
   * dem Modal im Baum, also verlöre er. Am gebauten Stylesheet nachgemessen, nicht angenommen.
   *
   * `!mt-0` gehört mit dazu: Tailwinds `space-y-*` der Seitenvorlage wirkt über
   * `> :not([hidden]) ~ :not([hidden])` und trifft auch `position: fixed`-Kinder. Die 24px würden
   * sich auf den `top-4`-Versatz zur Fensterkante addieren. Im Browser gegen das gebaute
   * Stylesheet nachgemessen.
   *
   * Wer den Balken NICHT über einem Modal braucht, lässt die Eigenschaft weg: im Fluss stehend ist
   * und bleibt die Mehrheitsfassung.
   */
  schwebend?: boolean;
}

/** Mehrheitsfassung: Rahmen für Text plus Schließen-Knopf, im Textfluss. */
const FLÄCHE_GRUND = 'flex items-start justify-between gap-4 p-4 rounded-lg';

/** Zusatz für `schwebend` — Begründung jedes Bestandteils an der Eigenschaft oben. */
const FLÄCHE_SCHWEBEND = 'fixed top-4 right-4 z-[60] !mt-0 shadow-lg';

export const Benachrichtigung: React.FC<BenachrichtigungProps> = ({
  inhalt,
  onSchließen,
  schwebend = false,
}) => {
  const istErfolg = inhalt?.type === 'success';

  return (
    <>
      {/* ANSAGE-EBENE — dauerhaft im DOM, auch wenn nichts anliegt.
          ⚠️ Vorher stand hier `if (!inhalt) return null`, und die Live-Region wurde GEMEINSAM mit
          ihrem Text montiert. Genau das ist der Fall, den die ARIA-Praxis ausschließt: eine
          Vorlesehilfe beobachtet Live-Regionen, die sie schon kennt; eine Region, die mitsamt Text
          neu erscheint, wird häufig gar nicht angesagt. Diese beiden Container stehen deshalb ab
          dem ersten Rendern da und bekommen später nur noch Text hineingereicht.
          Zwei Container statt einem, weil auch die DRINGLICHKEIT nicht im selben Commit wechseln
          darf wie der Text — sonst gilt derselbe Einwand nochmal eine Ebene tiefer.
          `sr-only` ist `position: absolute` und damit ohne Layout-Wirkung: die leeren Container
          verschieben nichts, auch nicht unter `space-y-*`. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {inhalt?.type === 'success' ? inhalt.message : ''}
      </div>
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {inhalt?.type === 'error' ? inhalt.message : ''}
      </div>

      {/* SICHT-EBENE — trägt die Rolle, damit sie beim Navigieren als Status bzw. Meldung
          angekündigt wird, aber ausdrücklich `aria-live="off"`: das Ansagen erledigt die Ebene
          darüber. Ohne das `off` läse eine Vorlesehilfe jede Meldung doppelt.
          Das `key` ist Pflicht, keine Kosmetik: ohne es benutzt React beim Übergang Fehler→Erfolg
          denselben `<div>` weiter und tauscht nur das Attribut von `alert` auf `status`. Eine Rolle,
          die an einem bestehenden Knoten wechselt, ist im Baum eine Lüge — Vorlesehilfen merken
          sich die Rolle beim Betreten. Mit `key` verschwindet der alte Knoten und ein neuer kommt. */}
      {inhalt && (
        <div
          key={inhalt.type}
          role={istErfolg ? 'status' : 'alert'}
          aria-live="off"
          className={[
            FLÄCHE_GRUND,
            schwebend ? FLÄCHE_SCHWEBEND : '',
            istErfolg ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span>{inhalt.message}</span>
          {/* Nur der Fehler braucht einen Weg hinaus — der Erfolg verblasst von allein. */}
          {!istErfolg && (
            <button
              type="button"
              onClick={onSchließen}
              className="btn btn-outline cursor-pointer flex-shrink-0"
              aria-label="Fehlermeldung schließen"
            >
              Schließen
            </button>
          )}
        </div>
      )}
    </>
  );
};
